import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import api, { documentAPI, Document } from '../services/api';
import {
  FolderIcon,
  EditIcon,
  CheckIcon,
  CloseIcon,
  CheckCircleIcon,
  LoaderIcon,
  AlertCircleIcon,
  FileTextIcon,
  UserIcon,
  ClockIcon,
  HomeIcon
} from '../components/Icons';
import './Catalog.css';

const Catalog: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [renameError, setRenameError] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);
  const [contractsUsed, setContractsUsed] = useState<number>(0);
  const [contractsLimit, setContractsLimit] = useState<number>(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['documents', 'dashboard', 'common']);
  const { getLocalizedPath, currentLanguage } = useLanguage();

  useEffect(() => {
    loadDocuments();
    loadUsage();
  }, []);

  useEffect(() => {
    const hasProcessingDocs = documents.some(doc => doc.status === 'processing');

    if (!hasProcessingDocs) {
      return;
    }

    const interval = setInterval(() => {
      loadDocuments();
    }, 3000);

    return () => clearInterval(interval);
  }, [documents]);

  const loadDocuments = async () => {
    try {
      const res = await documentAPI.list();
      setDocuments(res.data.documents);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsage = async () => {
    try {
      const res = await api.get('/documents/usage/remaining');
      setContractsUsed(res.data.contracts_used || 0);
      setContractsLimit(res.data.contracts_limit || 0);
    } catch (err) {
      console.error('Failed to load usage:', err);
    }
  };

  const handleUploadClick = () => {
    setUploadError('');
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setUploadError(t('dashboard:upload.errors.invalidFormat'));
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      setUploadError(t('dashboard:upload.errors.fileTooLarge'));
      return;
    }

    setUploadError('');
    setSelectedFile(file);
    setShowRoleModal(true);
  };

  const resetUploadInput = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRoleSelection = async (role: 'tenant' | 'landlord') => {
    if (!selectedFile) return;

    setShowRoleModal(false);
    setUploading(true);

    try {
      const res = await documentAPI.upload(selectedFile, role, i18n.language);
      const createdDoc = (res as any)?.data?.document || (res as any)?.data || null;
      resetUploadInput();
      if (createdDoc?.id) {
        if (createdDoc?.is_duplicate) {
          setUploadError(createdDoc.message || t('dashboard:upload.duplicateWarning'));
        }
        navigate(getLocalizedPath(`/documents/${createdDoc.id}`), { state: { document: createdDoc as Document } });
      } else {
        loadDocuments();
        loadUsage();
      }
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      const isTimeout = err.code === 'ECONNABORTED';
      if (isTimeout) {
        try {
          const list = await documentAPI.list();
          const latest = list.data.documents?.[0];
          if (latest?.id) {
            navigate(getLocalizedPath(`/documents/${latest.id}`), { state: { document: latest as Document } });
            setUploadError(t('dashboard:upload.timeoutWarning'));
            return;
          }
        } catch (listErr) {
          console.error('Timeout during upload and failed to load documents list:', listErr);
        }
      }
      const errorMessage = typeof errorDetail === 'string'
        ? errorDetail
        : errorDetail?.message || (isTimeout ? t('dashboard:upload.errors.timeout') : t('dashboard:upload.errors.uploadFailed'));
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const cancelRoleModal = () => {
    setShowRoleModal(false);
    resetUploadInput();
  };

  const startRename = (doc: Document) => {
    setEditingId(doc.id);
    setEditingName(doc.auto_generated_name);
    setRenameError('');
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingName('');
    setRenameError('');
  };

  const saveRename = async (doc: Document) => {
    const trimmed = editingName.trim();
    if (!trimmed) {
      setRenameError(t('common:errors.requiredField'));
      return;
    }

    const duplicate = documents.some(
      (existing) =>
        existing.id !== doc.id &&
        existing.auto_generated_name.trim().toLowerCase() === trimmed.toLowerCase()
    );

    if (duplicate) {
      setRenameError(t('common:errors.generic'));
      return;
    }

    setSavingId(doc.id);
    try {
      const res = await documentAPI.rename(doc.id, trimmed);
      setDocuments((prev) =>
        prev.map((item) =>
          item.id === doc.id
            ? { ...item, auto_generated_name: res.data.name }
            : item
        )
      );
      cancelRename();
    } catch (err: any) {
      setRenameError(err?.response?.data?.detail || t('common:errors.generic'));
    } finally {
      setSavingId(null);
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString(currentLanguage === 'pt' ? 'pt-BR' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRiskSnapshot = (doc: Document) => {
    if (!doc.analysis || doc.status !== 'completed') return null;

    try {
      const risks = doc.analysis?.risks || [];
      const high = risks.filter((r: any) => r.severity === 'high').length;
      const medium = risks.filter((r: any) => r.severity === 'medium').length;
      const low = risks.filter((r: any) => r.severity === 'low').length;
      const total = high + medium + low;

      if (total === 0) return { high: 0, medium: 0, low: 0, total: 0, hasRisks: false };

      return { high, medium, low, total, hasRisks: true };
    } catch {
      return null;
    }
  };

  const getPropertyAddress = (doc: Document) => {
    if (!doc.analysis || doc.status !== 'completed') return null;

    try {
      // Property data is in analysis.parties.property or analysis.parties.imovel
      const parties = doc.analysis?.parties?.parties || doc.analysis?.parties;
      if (!parties || typeof parties !== 'object') return null;

      // Look for property/imovel in parties object
      let property = parties.property || parties.imovel;

      if (!property || typeof property !== 'object') return null;

      const address = property.address || property.endereco;
      if (!address || address === 'Não especificado' || address === 'Não aplica') return null;

      return address;
    } catch (error) {
      console.error('Error getting property address:', error);
      return null;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'processing':
        return 'status-processing';
      case 'failed':
        return 'status-failed';
      default:
        return '';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return t('common:status.completed');
      case 'processing':
        return t('common:status.processing');
      case 'failed':
        return t('common:status.failed');
      default:
        return status;
    }
  };

  const renderStatusIcon = (status: string) => {
    const iconSize = 12;
    switch (status) {
      case 'completed':
        return <CheckCircleIcon size={iconSize} />;
      case 'processing':
        return <LoaderIcon size={iconSize} className="status-icon-spin" />;
      case 'failed':
        return <AlertCircleIcon size={iconSize} />;
      default:
        return null;
    }
  };

  const getDocumentTypeName = (type: string) => {
    switch (type) {
      case 'rental':
        return t('catalog.card.contractType');
      default:
        return type;
    }
  };

  const getUserRoleName = (role: string) => {
    switch (role) {
      case 'tenant':
        return t('common:roles.tenant');
      case 'landlord':
        return t('common:roles.landlord');
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>{t('common:buttons.loading')}</p>
      </div>
    );
  }

  return (
    <div className="container catalog-page">
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={uploading}
      />

      {showRoleModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="modal-title">{t('dashboard:roleSelection.title')}</h2>
            <p className="modal-subtitle">
              {t('dashboard:roleSelection.subtitle')}
            </p>
            <div className="role-buttons">
              <button
                className="role-button tenant-button"
                onClick={() => handleRoleSelection('tenant')}
              >
                <div className="role-icon"><UserIcon size={32} /></div>
                <div className="role-title">{t('dashboard:roleSelection.tenant')}</div>
                <div className="role-description">{t('dashboard:roleSelection.tenantDesc')}</div>
              </button>
              <button
                className="role-button landlord-button"
                onClick={() => handleRoleSelection('landlord')}
              >
                <div className="role-icon"><HomeIcon size={32} /></div>
                <div className="role-title">{t('dashboard:roleSelection.landlord')}</div>
                <div className="role-description">{t('dashboard:roleSelection.landlordDesc')}</div>
              </button>
            </div>
            <button className="modal-close" onClick={cancelRoleModal}>
              {t('dashboard:roleSelection.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="catalog-header">
        <div className="catalog-header-left">
          <h1 className="catalog-title">{t('catalog.title')}</h1>
          <p className="catalog-subtitle">{t('catalog.subtitle')}</p>
          {contractsLimit > 0 && (
            <div style={{
              fontSize: '0.85rem',
              color: contractsUsed >= contractsLimit ? '#dc2626' : contractsUsed >= contractsLimit * 0.8 ? '#ea580c' : '#64748b',
              fontWeight: '500',
              marginTop: '0.5rem'
            }}>
              {contractsUsed} / {contractsLimit}
            </div>
          )}
        </div>
        <div>
          <button
            className="btn btn-primary"
            onClick={handleUploadClick}
            disabled={uploading || (contractsLimit > 0 && contractsUsed >= contractsLimit)}
          >
            {uploading ? t('dashboard:upload.uploading') : `+ ${t('catalog.uploadFirst')}`}
          </button>
        </div>
      </div>

      {uploadError && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {uploadError}
        </div>
      )}

      {documents.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <FolderIcon size={64} />
          </div>
          <p className="empty-state-title">{t('catalog.empty')}</p>
          <p className="empty-state-description">
            {t('catalog.uploadFirst')}
          </p>
          <button
            className="btn btn-primary mt-3"
            onClick={handleUploadClick}
            disabled={uploading}
          >
            {uploading ? t('dashboard:upload.uploading') : t('common:buttons.upload')}
          </button>
        </div>
      ) : (
        <div className="documents-grid">
          {documents.map((doc) => {
            const riskSnapshot = getRiskSnapshot(doc);
            const propertyAddress = getPropertyAddress(doc);

            return (
              <div
                key={doc.id}
                className={`document-card ${doc.status === 'processing' ? 'card-processing' : ''}`}
                onClick={() => navigate(getLocalizedPath(`/documents/${doc.id}`))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(getLocalizedPath(`/documents/${doc.id}`));
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={doc.auto_generated_name}
              >
                {/* Status Badge - Top Right */}
                <span className={`document-status ${getStatusClass(doc.status)}`}>
                  <span className="status-icon">{renderStatusIcon(doc.status)}</span>
                  {getStatusText(doc.status)}
                </span>

                {/* Card Header - Title & Date */}
                <div className="document-card-header">
                  {editingId === doc.id ? (
                    <div
                      className="document-name-edit"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <input
                        className="document-name-input"
                        value={editingName}
                        onChange={(event) => {
                          setEditingName(event.target.value);
                          if (renameError) {
                            setRenameError('');
                          }
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            saveRename(doc);
                          }
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            cancelRename();
                          }
                        }}
                        maxLength={80}
                        autoFocus
                      />
                      <div className="document-name-actions">
                        <button
                          className="document-name-btn save"
                          type="button"
                          onClick={() => saveRename(doc)}
                          disabled={savingId === doc.id}
                          aria-label={t('common:buttons.save')}
                        >
                          <CheckIcon size={16} />
                        </button>
                        <button
                          className="document-name-btn cancel"
                          type="button"
                          onClick={cancelRename}
                          disabled={savingId === doc.id}
                          aria-label={t('common:buttons.cancel')}
                        >
                          <CloseIcon size={16} />
                        </button>
                      </div>
                      {renameError && (
                        <div className="document-name-error">{renameError}</div>
                      )}
                    </div>
                  ) : (
                    <div className="document-name-display">
                      <div className="document-title-with-edit">
                        <h3 className="document-title-primary">{doc.auto_generated_name}</h3>
                        <button
                          className="document-name-btn edit"
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            startRename(doc);
                          }}
                          aria-label={t('common:editContractName')}
                        >
                          <EditIcon size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Metadata - Compact */}
                <div className="document-metadata">
                  <div className="document-metadata-line">
                    <FileTextIcon size={14} />
                    {getDocumentTypeName(doc.document_type)}
                    {doc.user_role && (
                      <>
                        <span className="metadata-separator">·</span>
                        <UserIcon size={14} />
                        {getUserRoleName(doc.user_role)}
                      </>
                    )}
                  </div>
                  {propertyAddress && (
                    <div
                      className="document-metadata-line document-address"
                      data-full-address={propertyAddress}
                      title={propertyAddress}
                    >
                      <HomeIcon size={14} />
                      {propertyAddress}
                    </div>
                  )}
                  <div className="document-metadata-line">
                    <ClockIcon size={14} />
                    {formatDateTime(doc.created_at)}
                  </div>
                </div>

                {/* Spacer - Pushes risk section to bottom */}
                <div className="document-card-spacer"></div>

                {/* Risk Snapshot - Stuck to Bottom */}
                {riskSnapshot && riskSnapshot.hasRisks && (
                  <div className="document-risk-snapshot">
                    <div className="risk-snapshot-header">{t('catalog.card.risks')}</div>
                    <div className="risk-snapshot-items">
                      {riskSnapshot.high > 0 && (
                        <div className="risk-snapshot-item risk-high">
                          <span className="risk-snapshot-dot risk-high"></span>
                          <span className="risk-snapshot-label">{riskSnapshot.high} {t('common:riskLevels.high').toLowerCase()}</span>
                        </div>
                      )}
                      {riskSnapshot.medium > 0 && (
                        <div className="risk-snapshot-item risk-medium">
                          <span className="risk-snapshot-dot risk-medium"></span>
                          <span className="risk-snapshot-label">{riskSnapshot.medium} {t('common:riskLevels.medium').toLowerCase()}</span>
                        </div>
                      )}
                      {riskSnapshot.low > 0 && (
                        <div className="risk-snapshot-item risk-low">
                          <span className="risk-snapshot-dot risk-low"></span>
                          <span className="risk-snapshot-label">{riskSnapshot.low} {t('common:riskLevels.low').toLowerCase()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {riskSnapshot && !riskSnapshot.hasRisks && (
                  <div className="document-risk-snapshot">
                    <div className="risk-snapshot-header">{t('catalog.card.risks')}</div>
                    <div className="risk-snapshot-items">
                      <div className="risk-snapshot-item risk-none">
                        <span className="risk-snapshot-dot risk-low"></span>
                        <span className="risk-snapshot-label">{t('catalog.card.noRisks')}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Catalog;
